import { createContext, useContext } from 'react'

/**
 * Per-editor deferred-reveal state exposed to in-editor React surfaces (node
 * views) that live OUTSIDE the ProseMirror decoration pipeline — today only the
 * validated-tool node view, whose form-field suggestions must hide/show with the
 * same reveal toggle as the inline editor proposals.
 *
 * The inline proposals read reveal via the decoration gate in
 * `CollaborativeEditor` (decorations only paint while revealed). A node view has
 * no decorations to gate, so it needs the state directly. Provided by
 * `CollaborativeEditor` alongside `AIProposalActionsProvider`; when absent (a
 * bare/standalone editor with no review apparatus) `useSuggestionReveal`
 * defaults to `revealed: true` so such a surface behaves as it did before this
 * gate existed.
 */
export interface SuggestionReveal {
  /** Whether the suggestion review apparatus is active in this tab. */
  revealed: boolean
  /**
   * True while a reveal is painting (between "Review suggestions" and hydration
   * settling). The node view treats this like revealed so the form fills in
   * lockstep with the inline proposals.
   */
  revealing: boolean
}

const SuggestionRevealContext = createContext<SuggestionReveal | null>(null)

export const SuggestionRevealProvider = SuggestionRevealContext.Provider

export function useSuggestionReveal(): SuggestionReveal {
  return (
    useContext(SuggestionRevealContext) ?? { revealed: true, revealing: false }
  )
}
